import { View, Text, styled } from 'tamagui'
import { StatusDot } from './StatusDot'
import type { UserStatus } from '@/types'

const AvatarContainer = styled(View, {
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '$borderRadiusFull',
  overflow: 'visible',
  variants: {
    size: {
      sm: { width: 24, height: 24 },
      md: { width: 32, height: 32 },
      lg: { width: 40, height: 40 },
      xl: { width: 64, height: 64 },
    },
  } as const,
  defaultVariants: {
    size: 'md',
  },
})

const AvatarCircle = styled(View, {
  width: '100%',
  height: '100%',
  borderRadius: '$borderRadiusFull',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
})

const AvatarText = styled(Text, {
  fontFamily: '$fontBody',
  fontWeight: '600',
  color: '$surfaceForeground',
  variants: {
    size: {
      sm: { fontSize: 10 },
      md: { fontSize: 13 },
      lg: { fontSize: 16 },
      xl: { fontSize: 24 },
    },
  } as const,
  defaultVariants: {
    size: 'md',
  },
})

interface AvatarProps {
  name: string
  color?: string
  status?: UserStatus
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showStatus?: boolean
  isSpeaking?: boolean
}

export function Avatar({ name, color = '#2A3142', status, size = 'md', showStatus = true, isSpeaking }: AvatarProps) {
  const initial = name.charAt(0).toUpperCase()
  const statusSize = size === 'sm' ? 8 : size === 'md' ? 10 : size === 'xl' ? 14 : 12
  const statusOffset = size === 'sm' ? -1 : size === 'xl' ? 2 : 0

  return (
    <AvatarContainer size={size}>
      <AvatarCircle
        backgroundColor={color}
        {...(isSpeaking && {
          borderWidth: 2,
          borderColor: '$warning',
          // @ts-expect-error -- boxShadow web-only
          style: { boxShadow: '0 0 12px rgba(232, 168, 64, 0.3), 0 0 4px rgba(232, 168, 64, 0.15)' },
        })}
      >
        <AvatarText size={size}>{initial}</AvatarText>
      </AvatarCircle>
      {showStatus && status && (
        <View
          position="absolute"
          bottom={statusOffset}
          right={statusOffset}
          zIndex={1}
          backgroundColor="$surface"
          borderRadius="$borderRadiusFull"
          padding={1.5}
        >
          <StatusDot status={status} size={statusSize} />
        </View>
      )}
    </AvatarContainer>
  )
}
